import { isUndefined } from 'lodash';
import { connect } from '@client/decorators/common';
import { startFetchPublicActDetails, unsubscribeAct } from '@client/slices/entities/acts.reducer';
import { getActById, getActByByVanityUrl } from '@client/selectors/entities/acts';
import { isOwnerOrCollaborator } from '@client/selectors/helpers/session';
import { createSelectorFactory } from '@client/selectors/lib';
import { withData } from '@client/decorators/common/withData';
import { withActListeners } from './with-act.listeners';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type { PublicAct } from '@sda/interfaces/act';
import type { WithDataOptions } from '@client/decorators/common/withData';
import type { RootState } from '@client/store';

const getActByIdFactory = createSelectorFactory(getActById);
const getActByByVanityUrlFactory = createSelectorFactory(getActByByVanityUrl);
const isOwnerOrCollaboratorFactory = createSelectorFactory(isOwnerOrCollaborator);

/**
 * Decorate a component with a call to fetch an artist by ID
 */
export const withAct = <TProps>(options?: WithDataOptions) => {
  const { useVanityUrl = false } = options || {};

  const mapDispatchToProps = {
    fetch: startFetchPublicActDetails,
    unfetch: unsubscribeAct,
  };

  const mapStateToProps = () => {
    const getActByIdSelector = getActByIdFactory();
    const getActByByVanityUrlSelector = getActByByVanityUrlFactory();
    const isOwnerOrCollaboratorSelector = isOwnerOrCollaboratorFactory();

    return (state: RootState, { match, actId }) => {
      const id = actId || match.params.actId;

      let act: PublicAct & FetchDataType;
      let canonicalActId = null;

      if (useVanityUrl) {
        act = getActByByVanityUrlSelector(state, id);
      } else {
        canonicalActId = actId;

        act = getActByIdSelector(state, id);
      }

      if (act) {
        canonicalActId = act.id;
      }

      return {
        act,
        actId: canonicalActId,
        current: canonicalActId,
        canAccess: !!(act && !isUndefined(act.privateUpdatedAt)),
        isOwnerOrCollaborator: isOwnerOrCollaboratorSelector(state, act),
      };
    };
  };

  return (
    WrappedComponent: React.ElementType<TProps & ReturnType<ReturnType<typeof mapStateToProps>>>,
  ) => connect(mapStateToProps, mapDispatchToProps)(withData(WrappedComponent, options, withActListeners));
};
